import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { SITE } from "../data/site";
import { toast } from "sonner";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LogOut, Plus, Pencil, Trash2, Bike, CalendarDays, Bell, Check, Ban, MessageCircle, Mail } from "lucide-react";
import { PushToggle } from "../components/PushToggle";

const EMPTY = {
  name: "", brand: "", cc: 110, category: "motorcycle", transmission: "Automatic",
  engine: "Four-stroke", passengers: 2, description: "", image: "", images: [],
  quantity: 2, featured: false, available: true,
};

function MotoForm({ initial, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(
    initial
      ? { ...initial, images: initial.images?.length ? initial.images : (initial.image ? [initial.image] : []) }
      : EMPTY
  );
  const [uploading, setUploading] = useState(false);
  const isEdit = !!initial?.id;
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setForm((f) => {
          const images = [...(f.images || []), data.url];
          return { ...f, images, image: f.image || images[0] };
        });
      }
      toast.success(files.length > 1 ? `${files.length} photos uploaded` : "Photo uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (i) => setForm((f) => {
    const images = f.images.filter((_, j) => j !== i);
    return { ...f, images, image: images[0] || "" };
  });

  const setCover = (i) => setForm((f) => {
    const images = [f.images[i], ...f.images.filter((_, j) => j !== i)];
    return { ...f, images, image: images[0] };
  });

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      cc: parseInt(form.cc) || 0,
      passengers: parseInt(form.passengers) || 2,
      quantity: parseInt(form.quantity) || 1,
      images: form.images || [],
    };
    try {
      if (isEdit) await api.put(`/motorcycles/${initial.id}`, payload);
      else await api.post("/motorcycles", payload);
      toast.success(isEdit ? "Motorcycle updated" : "Motorcycle added");
      setOpen(false);
      onSaved();
    } catch {
      toast.error("Could not save motorcycle");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {isEdit ? (
          <button data-testid={`admin-edit-${initial.id}`} className="text-brand-blue hover:text-brand-orange p-2"><Pencil size={16} /></button>
        ) : (
          <button data-testid="admin-add-moto" className="inline-flex items-center gap-2 bg-brand-orange text-white font-heading font-bold py-2.5 px-5 rounded-full"><Plus size={18} /> Add Motorcycle</button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="admin-moto-form">
        <DialogHeader><DialogTitle className="font-heading">{isEdit ? "Edit" : "Add"} Motorcycle</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name *</Label><Input data-testid="form-name" value={form.name} onChange={upd("name")} required /></div>
            <div><Label>Brand</Label><Input value={form.brand} onChange={upd("brand")} /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>CC *</Label><Input data-testid="form-cc" type="number" value={form.cc} onChange={upd("cc")} required /></div>
            <div>
              <Label>Category</Label>
              <select value={form.category} onChange={upd("category")} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm">
                <option value="motorcycle">Motorcycle</option>
                <option value="scooter">Scooter</option>
                <option value="atv">ATV</option>
              </select>
            </div>
            <div><Label>Quantity</Label><Input type="number" value={form.quantity} onChange={upd("quantity")} /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Transmission</Label><Input value={form.transmission} onChange={upd("transmission")} /></div>
            <div><Label>Engine</Label><Input value={form.engine} onChange={upd("engine")} /></div>
            <div><Label>Passengers</Label><Input type="number" value={form.passengers} onChange={upd("passengers")} /></div>
          </div>
          <div>
            <Label>Motorcycle photos</Label>
            <div className="flex flex-wrap gap-2 mt-1 mb-3">
              {(form.images || []).map((src, i) => (
                <div key={i} data-testid={`form-image-thumb-${i}`} className="relative w-20 h-20 rounded-lg bg-slate-50 border border-slate-200 overflow-hidden group">
                  <img src={src} alt={`photo ${i + 1}`} className="w-full h-full object-contain" />
                  {i === 0 && (
                    <span className="absolute bottom-0 inset-x-0 bg-brand-blue text-white text-[9px] font-bold text-center py-0.5">COVER</span>
                  )}
                  <button type="button" onClick={() => removeImage(i)} data-testid={`form-image-remove-${i}`} title="Remove"
                    className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Trash2 size={11} />
                  </button>
                  {i !== 0 && (
                    <button type="button" onClick={() => setCover(i)} title="Set as cover"
                      className="absolute top-0.5 left-0.5 bg-white/90 text-brand-blue rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Bike size={11} />
                    </button>
                  )}
                </div>
              ))}
              <label
                htmlFor="moto-file-input"
                data-testid="form-upload-button"
                className={`w-20 h-20 rounded-lg border-2 border-dashed border-brand-blue/40 hover:border-brand-blue text-brand-blue flex flex-col items-center justify-center cursor-pointer transition-colors ${uploading ? "opacity-60 pointer-events-none" : ""}`}
              >
                <Plus size={20} />
                <span className="text-[10px] font-bold mt-0.5">{uploading ? "..." : "Add"}</span>
              </label>
              <input id="moto-file-input" type="file" accept="image/*" multiple onChange={onFile} data-testid="form-file-input" className="hidden" />
            </div>
            <p className="text-xs text-slate-400 mb-1">Upload from your phone gallery (JPG, PNG, WEBP, HEIC — max 10MB each). First photo is the cover. Or paste a URL:</p>
            <Input data-testid="form-image" value={form.image} onChange={upd("image")} placeholder="/images/motos/... or https://..." />
          </div>
          <div><Label>Description</Label><Input value={form.description} onChange={upd("description")} /></div>
          <div className="flex items-center gap-6 pt-1">
            <label className="flex items-center gap-2 text-sm font-heading font-semibold"><Switch checked={form.available} onCheckedChange={(v) => setForm({ ...form, available: v })} /> Available</label>
            <label className="flex items-center gap-2 text-sm font-heading font-semibold"><Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} /> Featured</label>
          </div>
          <DialogFooter>
            <button type="submit" data-testid="form-save" className="w-full bg-brand-blue text-white font-heading font-bold py-3 rounded-full">{isEdit ? "Save Changes" : "Add Motorcycle"}</button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [motos, setMotos] = useState([]);
  const [bookings, setBookings] = useState([]);

  const load = () => {
    api.get("/motorcycles").then((r) => setMotos(r.data)).catch(() => {});
    api.get("/bookings").then((r) => setBookings(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (user === false) navigate("/login");
    if (user) load();
  }, [user]); // eslint-disable-line

  // Poll for new reservation requests every 20s so the notification badge stays live.
  useEffect(() => {
    if (!user) return;
    const id = setInterval(() => {
      api.get("/bookings").then((r) => {
        setBookings((prev) => {
          const prevPending = prev.filter((b) => b.status === "pending").length;
          const nextPending = r.data.filter((b) => b.status === "pending").length;
          if (nextPending > prevPending) {
            toast.info(`🔔 ${nextPending - prevPending} new reservation request${nextPending - prevPending > 1 ? "s" : ""}!`);
          }
          return r.data;
        });
      }).catch(() => {});
    }, 20000);
    return () => clearInterval(id);
  }, [user]); // eslint-disable-line

  const pendingCount = bookings.filter((b) => b.status === "pending").length;

  // Build a WhatsApp link to message the CLIENT directly, pre-filled with their request.
  const waClient = (b) => {
    const digits = (b.phone || "").replace(/\D/g, "");
    if (!digits) return null;
    const text = encodeURIComponent(
      `Hi ${b.name}, thanks for your reservation request with Nica Moto Rentals for ${b.motorcycle_name || "a moto"} (${b.start_date} - ${b.end_date}). `
    );
    return `https://wa.me/${digits}?text=${text}`;
  };

  const toggle = async (m) => {
    try {
      await api.patch(`/motorcycles/${m.id}/availability`, { available: !m.available });
      setMotos((prev) => prev.map((x) => (x.id === m.id ? { ...x, available: !x.available } : x)));
      toast.success(`${m.name} marked ${!m.available ? "Available" : "Rented"}`);
    } catch { toast.error("Update failed"); }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete ${m.name}?`)) return;
    try {
      await api.delete(`/motorcycles/${m.id}`);
      setMotos((prev) => prev.filter((x) => x.id !== m.id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const setStatus = async (b, status) => {
    try {
      await api.patch(`/bookings/${b.id}`, { status });
      setBookings((prev) => prev.map((x) => (x.id === b.id ? { ...x, status } : x)));
      if (status === "confirmed") toast.success(`Reservation confirmed — ${b.motorcycle_name || "moto"} marked Rented on the site`);
      else if (status === "cancelled") toast.success(`Reservation cancelled — ${b.motorcycle_name || "moto"} released back to Available`);
      load(); // refresh inventory so availability reflects on the front end
    } catch { toast.error("Update failed"); }
  };

  if (!user) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading...</div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={SITE.logo} alt="logo" className="h-10" />
            <span className="font-heading font-black text-brand-gray hidden sm:block">Admin Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <PushToggle />
            <div className="relative" data-testid="admin-notif-bell" title={`${pendingCount} pending request(s)`}>
              <Bell size={22} className={pendingCount > 0 ? "text-brand-orange" : "text-slate-400"} />
              {pendingCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-heading font-black rounded-full">
                  {pendingCount}
                </span>
              )}
            </div>
            <button onClick={() => { logout(); navigate("/login"); }} data-testid="admin-logout" className="inline-flex items-center gap-2 text-brand-gray hover:text-brand-orange font-heading font-semibold">
              <LogOut size={18} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Tabs defaultValue="inventory">
          <TabsList className="mb-8">
            <TabsTrigger value="inventory" data-testid="tab-inventory"><Bike size={16} className="mr-2" /> Inventory ({motos.length})</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings" className="relative">
              <CalendarDays size={16} className="mr-2" /> Bookings ({bookings.length})
              {pendingCount > 0 && (
                <span data-testid="pending-badge" className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-xs font-heading font-black rounded-full animate-pulse">
                  {pendingCount}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="inventory">
            <div className="flex justify-end mb-5"><MotoForm onSaved={load} /></div>
            <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-left font-heading">
                    <tr>
                      <th className="p-4">Moto</th><th className="p-4">CC</th><th className="p-4">Category</th>
                      <th className="p-4">Status</th><th className="p-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {motos.map((m) => (
                      <tr key={m.id} data-testid={`admin-row-${m.id}`} className="hover:bg-slate-50">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <img src={m.image} alt={m.name} className="w-12 h-12 object-contain bg-slate-50 rounded-lg" />
                            <div>
                              <span className="font-heading font-bold text-brand-gray block">{m.name}</span>
                              <span data-testid={`admin-units-${m.id}`} className="text-xs text-slate-400">
                                {m.available_units ?? (m.available ? (m.quantity ?? 1) : 0)} of {m.quantity ?? 1} available
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="p-4">{m.cc}cc</td>
                        <td className="p-4 capitalize">{m.category}</td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <Switch checked={m.available} onCheckedChange={() => toggle(m)} data-testid={`admin-toggle-${m.id}`} />
                            <span className={`text-xs font-bold ${m.available ? "text-green-600" : "text-red-500"}`}>{m.available ? "Available" : "Rented"}</span>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center justify-end gap-1">
                            <MotoForm initial={m} onSaved={load} />
                            <button onClick={() => remove(m)} data-testid={`admin-delete-${m.id}`} className="text-red-400 hover:text-red-600 p-2"><Trash2 size={16} /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="bookings">
            <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-left font-heading">
                    <tr>
                      <th className="p-4">Customer</th><th className="p-4">Moto</th><th className="p-4">Dates</th>
                      <th className="p-4">Contact</th><th className="p-4">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {bookings.map((b) => (
                      <tr key={b.id} data-testid={`booking-row-${b.id}`} className={`hover:bg-slate-50 ${b.status === "pending" ? "bg-amber-50/40" : ""}`}>
                        <td className="p-4 font-heading font-bold text-brand-gray">{b.name}</td>
                        <td className="p-4">{b.motorcycle_name || "—"}</td>
                        <td className="p-4">{b.start_date} → {b.end_date}</td>
                        <td className="p-4">
                          <div className="flex flex-col gap-1.5">
                            {b.phone && (
                              <a href={waClient(b)} target="_blank" rel="noreferrer" data-testid={`booking-whatsapp-${b.id}`}
                                title="Message client on WhatsApp"
                                className="inline-flex items-center gap-1.5 bg-[#25D366] hover:bg-[#1da851] text-white text-xs font-heading font-bold px-3 py-1.5 rounded-full transition-colors w-fit">
                                <MessageCircle size={13} /> {b.phone}
                              </a>
                            )}
                            {b.email && (
                              <a href={`mailto:${b.email}`} data-testid={`booking-email-${b.id}`}
                                className="inline-flex items-center gap-1.5 text-slate-500 hover:text-brand-blue text-xs w-fit">
                                <Mail size={13} /> {b.email}
                              </a>
                            )}
                            {!b.phone && !b.email && <span className="text-slate-400">—</span>}
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            <select value={b.status} onChange={(e) => setStatus(b, e.target.value)} data-testid={`booking-status-${b.id}`} className={`border rounded-full px-3 py-1 text-xs font-bold capitalize ${b.status === "confirmed" ? "text-green-600 border-green-200" : b.status === "cancelled" ? "text-red-500 border-red-200" : "text-amber-600 border-amber-200"}`}>
                              <option value="pending">Pending</option>
                              <option value="confirmed">Confirmed</option>
                              <option value="cancelled">Cancelled</option>
                            </select>
                            {b.status === "pending" && (
                              <>
                                <button onClick={() => setStatus(b, "confirmed")} data-testid={`booking-approve-${b.id}`} title="Approve (marks moto Rented)"
                                  className="inline-flex items-center gap-1 bg-green-500 hover:bg-green-600 text-white text-xs font-heading font-bold px-3 py-1.5 rounded-full transition-colors">
                                  <Check size={14} /> Approve
                                </button>
                                <button onClick={() => setStatus(b, "cancelled")} data-testid={`booking-decline-${b.id}`} title="Decline"
                                  className="inline-flex items-center gap-1 bg-slate-200 hover:bg-red-100 text-slate-600 hover:text-red-600 text-xs font-heading font-bold px-3 py-1.5 rounded-full transition-colors">
                                  <Ban size={14} /> Decline
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {bookings.length === 0 && (
                      <tr><td colSpan={5} className="p-10 text-center text-slate-400">No booking requests yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
